import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Nullable,
  Attachment,
  PlannerComment,
  PlannerDashboard,
  PlannerLabel,
  PlannerTask,
  ApiResponse,
  PlannerTaskRelation,
} from '@vision/ui/interfaces';
import { PlannerEndpoints } from '@vision/ui/services/planner/planner.endpoints';
import { PlannerDashboardEndpoints } from '@vision/ui/services/planner/dashboards/dashboards.endpoints';
import { PlannerTaskEndpoints } from '@vision/ui/services/planner/tasks/tasks.endpoints';
import { PlannerTaskCommentEndpoints } from '@vision/ui/services/planner/tasks/task-comments.endpoints';
import { PlannerTaskLabelEndpoints } from '@vision/ui/services/planner/tasks/task-labels.endpoints';
import { PlannerTaskRelationEndpoints } from '@vision/ui/services/planner/tasks/task-relations.endpoints';
import { PlannerTaskWatcherEndpoints } from '@vision/ui/services/planner/tasks/task-watchers.endpoints';
import { PlannerTaskStatus } from '@vision/ui/enums';
import { setLastSelectedBoardId, getLastSelectedBoardId } from '@vision/ui/utils';

export interface PlannerStateType {
  attachmentsToUpload?: Attachment[];
  comments?: PlannerComment[];
  editingTask: Nullable<PlannerTask>;
  labels: PlannerLabel[];
  plannerBoardModalMode: 'add' | 'edit';
  plannerBoardModalOpened: boolean;
  plannerBoards: PlannerDashboard[];
  plannerId: string;
  plannerTaskDrawerOpened: boolean;
  selectedBoardId: string;
  selectedTaskStatus: PlannerTaskStatus;
  taskRelationToAdd: PlannerTaskRelation;
  tasks: ApiResponse<PlannerTask[]>;
  tasksLoaded: boolean;
}

const initialState: PlannerStateType = {
  attachmentsToUpload: [],
  comments: [],
  editingTask: null,
  labels: [],
  plannerBoardModalMode: 'add',
  plannerBoardModalOpened: false,
  plannerBoards: [] as PlannerDashboard[],
  plannerId: '',
  plannerTaskDrawerOpened: false,
  selectedBoardId: '',
  selectedTaskStatus: PlannerTaskStatus.NOT_STARTED,
  taskRelationToAdd: null,
  tasks: { data: [], meta: null, object: 'collection' },
  tasksLoaded: false,
};

export const PlannerState = createSlice({
  name: 'PlannerState',
  initialState,
  reducers: {
    openTaskDrawer: (state, action: PayloadAction<{ status?: PlannerTaskStatus }>) => {
      state.plannerTaskDrawerOpened = true;

      if (action.payload.status) {
        state.selectedTaskStatus = action.payload.status;
      }
    },
    closeTaskDrawer: (state) => {
      state.plannerTaskDrawerOpened = false;
      state.editingTask = null;
      state.attachmentsToUpload = [];
      state.comments = [];
      state.taskRelationToAdd = null;
    },
    openAddPlannerBoardModal: (state, action: PayloadAction<{ mode: 'add' | 'edit' }>) => {
      state.plannerBoardModalOpened = true;
      state.plannerBoardModalMode = action.payload.mode;
    },
    closeAddPlannerBoardModal: (state) => {
      state.plannerBoardModalOpened = false;
    },
    setSelectedBoardId: (state, action: PayloadAction<string>) => {
      state.selectedBoardId = action.payload;

      setLastSelectedBoardId(action.payload);
    },
    setPlannerId: (state, action: PayloadAction<{ plannerId: string }>) => {
      state.plannerId = action.payload.plannerId;
    },
    setEditingTaskAndOpenDrawer: (state, action: PayloadAction<Nullable<PlannerTask>>) => {
      state.editingTask = action.payload;
      state.plannerTaskDrawerOpened = true;
    },
    addAttachmentsToUpload: (state, action: PayloadAction<{ files: Attachment[] }>) => {
      const { attachmentsToUpload } = state;

      if (attachmentsToUpload) {
        state.attachmentsToUpload = [...attachmentsToUpload, ...action.payload.files];
      } else {
        state.attachmentsToUpload = action.payload.files;
      }
    },
    removeAttachmentToUpload: (state, action: PayloadAction<string>) => {
      const { attachmentsToUpload } = state;

      if (attachmentsToUpload) {
        state.attachmentsToUpload = attachmentsToUpload.filter((file) => file.id !== action.payload);
      }
    },
    setTaskRelationToAdd: (state, action: PayloadAction<PlannerTaskRelation>) => {
      state.taskRelationToAdd = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(PlannerEndpoints.endpoints.getPlanners.matchFulfilled, (state, { payload }) => {
      if (!payload.data?.length) return;

      const { id, dashboards, default_dashboard_id } = payload.data[0];
      const lastSelectedBoardId = getLastSelectedBoardId();

      // Check if last selected board exists in the returned boards
      const selectedBoardId =
        lastSelectedBoardId && dashboards.some((board) => board.id === lastSelectedBoardId)
          ? lastSelectedBoardId
          : default_dashboard_id;

      state.plannerId = id;
      state.plannerBoards = dashboards;
      state.selectedBoardId = selectedBoardId;

      setLastSelectedBoardId(selectedBoardId);
    });
    builder.addMatcher(PlannerDashboardEndpoints.endpoints.createDashboard.matchFulfilled, (state, { payload }) => {
      if (!payload.data) return;

      state.selectedBoardId = payload.data.id;
      setLastSelectedBoardId(payload.data.id);

      state.plannerBoardModalOpened = false;
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.createTask.matchFulfilled, (state) => {
      state.plannerTaskDrawerOpened = false;
      state.editingTask = null;
      state.attachmentsToUpload = [];
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.getDashboardTasks.matchPending, (state) => {
      state.tasksLoaded = false;
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.getDashboardTasks.matchFulfilled, (state) => {
      state.tasksLoaded = true;
    });
    builder.addMatcher(PlannerTaskCommentEndpoints.endpoints.createComment.matchFulfilled, (state) => {
      state.attachmentsToUpload = [];
    });
    builder.addMatcher(PlannerTaskCommentEndpoints.endpoints.getComments.matchFulfilled, (state, { payload }) => {
      state.comments = payload.data;
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.getDashboardTasks.matchFulfilled, (state, { payload }) => {
      state.tasks = payload;
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.showTask.matchFulfilled, (state, { payload }) => {
      state.tasks.data = state.tasks.data.map((task) =>
        task.id === payload.data.id ? { ...task, ...payload.data } : task,
      );
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.updateTask.matchPending, (state, { meta }) => {
      state.tasks.data = state.tasks.data.map((task) =>
        task.id === meta.arg.originalArgs.taskId ? { ...task, ...meta.arg.originalArgs.data } : task,
      );
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.updateTask.matchFulfilled, (state, { payload }) => {
      state.tasks.data = state.tasks.data.map((task) => (task.id === payload.data.id ? payload.data : task));
      state.editingTask = payload.data;
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.createTaskAttachment.matchFulfilled, (state, { payload }) => {
      state.tasks.data = state.tasks.data.map((task) => {
        if (task.id === payload.data.id) {
          return {
            ...task,
            attachments: payload.data.attachments,
          };
        }
        return task;
      });
    });
    builder.addMatcher(PlannerTaskEndpoints.endpoints.deleteAttachment.matchFulfilled, (state, { meta }) => {
      state.tasks.data = state.tasks.data.map((task) => {
        if (task.id === meta.arg.originalArgs.taskId) {
          return {
            ...task,
            attachments: task.attachments.filter((attachment) => attachment.blob.id !== meta.arg.originalArgs.blobId),
          };
        }
        return task;
      });

      if (state.editingTask && state.editingTask.id === meta.arg.originalArgs.taskId) {
        state.editingTask = {
          ...state.editingTask,
          attachments: state.editingTask.attachments.filter(
            (attachment) => attachment.blob.id !== meta.arg.originalArgs.blobId,
          ),
        };

        if (state.comments?.length) {
          state.comments = state.comments.map((comment) => {
            return {
              ...comment,
              attachments: comment.attachments.filter(
                (attachment) => attachment.blob.id !== meta.arg.originalArgs.blobId,
              ),
            };
          });
        }
      }
    });
    builder.addMatcher(PlannerTaskLabelEndpoints.endpoints.getLabels.matchFulfilled, (state, { payload }) => {
      state.labels = payload.data;
    });
    builder.addMatcher(PlannerTaskLabelEndpoints.endpoints.createLabel.matchFulfilled, (state, { payload }) => {
      state.labels = [...state.labels, payload.data];
    });
    builder.addMatcher(
      PlannerTaskLabelEndpoints.endpoints.createTaskLabel.matchFulfilled,
      (state, { meta, payload }) => {
        state.tasks.data = state.tasks.data.map((task) => {
          if (task.id === meta.arg.originalArgs.taskId) {
            return {
              ...task,
              task_labels: [...task.task_labels, payload.data],
            };
          }
          return task;
        });

        if (state.editingTask && state.editingTask.id === meta.arg.originalArgs.taskId) {
          state.editingTask = {
            ...state.editingTask,
            task_labels: [...state.editingTask.task_labels, payload.data],
          };
        }
      },
    );
    builder.addMatcher(PlannerTaskLabelEndpoints.endpoints.deleteTaskLabel.matchFulfilled, (state, { meta }) => {
      state.tasks.data = state.tasks.data.map((task) => {
        if (task.id === meta.arg.originalArgs.taskId) {
          return {
            ...task,
            task_labels: task.task_labels.filter((taskLabel) => taskLabel.id !== meta.arg.originalArgs.taskLabelId),
          };
        }
        return task;
      });

      if (state.editingTask && state.editingTask.id === meta.arg.originalArgs.taskId) {
        state.editingTask = {
          ...state.editingTask,
          task_labels: state.editingTask.task_labels.filter(
            (taskLabel) => taskLabel.id !== meta.arg.originalArgs.taskLabelId,
          ),
        };
      }
    });
    builder.addMatcher(
      PlannerTaskWatcherEndpoints.endpoints.createWatcher.matchFulfilled,
      (state, { meta, payload }) => {
        state.tasks.data = state.tasks.data.map((task) => {
          if (task.id === meta.arg.originalArgs.taskId) {
            return {
              ...task,
              watchers: [...task.watchers, payload.data],
            };
          }
          return task;
        });

        if (state.editingTask && state.editingTask.id === meta.arg.originalArgs.taskId) {
          state.editingTask = {
            ...state.editingTask,
            watchers: [...state.editingTask.watchers, payload.data],
          };
        }
      },
    );
    builder.addMatcher(PlannerTaskWatcherEndpoints.endpoints.deleteWatcher.matchFulfilled, (state, { meta }) => {
      state.tasks.data = state.tasks.data.map((task) => {
        if (task.id === meta.arg.originalArgs.taskId) {
          return {
            ...task,
            watchers: task.watchers.filter((watcher) => watcher.id !== meta.arg.originalArgs.watcherId),
          };
        }
        return task;
      });

      if (state.editingTask && state.editingTask.id === meta.arg.originalArgs.taskId) {
        state.editingTask = {
          ...state.editingTask,
          watchers: state.editingTask.watchers.filter((watcher) => watcher.id !== meta.arg.originalArgs.watcherId),
        };
      }
    });
    builder.addMatcher(
      PlannerTaskRelationEndpoints.endpoints.createTaskRelation.matchFulfilled,
      (state, { meta, payload }) => {
        state.tasks.data = state.tasks.data.map((task) => {
          if (task.id === meta.arg.originalArgs.taskId) {
            return payload.data.task;
          }
          return task;
        });
      },
    );
    builder.addMatcher(PlannerTaskRelationEndpoints.endpoints.deleteTaskRelation.matchFulfilled, (state, { meta }) => {
      state.tasks.data = state.tasks.data.map((task) => {
        if (task.id === meta.arg.originalArgs.taskId) {
          return {
            ...task,
            task_relations: task.task_relations.filter(
              (taskRelation) => taskRelation.id !== meta.arg.originalArgs.taskRelationId,
            ),
          };
        }
        return task;
      });

      if (state.editingTask && state.editingTask.id === meta.arg.originalArgs.taskId) {
        state.editingTask = {
          ...state.editingTask,
          task_relations: state.editingTask.task_relations.filter(
            (taskRelation) => taskRelation.id !== meta.arg.originalArgs.taskRelationId,
          ),
        };
      }
    });
  },
});

export const {
  addAttachmentsToUpload,
  closeAddPlannerBoardModal,
  closeTaskDrawer,
  openAddPlannerBoardModal,
  openTaskDrawer,
  removeAttachmentToUpload,
  setEditingTaskAndOpenDrawer,
  setPlannerId,
  setSelectedBoardId,
  setTaskRelationToAdd,
} = PlannerState.actions;
